<template>
  <div class="custom-jsonforms-renderer">
    <!-- array renderer for root-level arrays -->
    <div v-if="isArray(schema)" class="array-renderer">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">{{ schema.title || $t('publication.editor.items') }}</h6>
        <button type="button" class="btn btn-dark btn-sm btn-pill" @click="addArrayItem()">
          {{ $t('publication.editor.add_item') }} <i class="ti ti-plus"></i>
        </button>
      </div>

      <div v-if="schema.description" class="form-text text-muted mb-3">
        {{ schema.description }}
      </div>

      <div v-for="(item, index) in getArrayData()" :key="index" class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0">{{ $t('publication.editor.item') }} {{ index + 1 }}</h6>
          <a type="button" class="icon-link" @click="removeArrayItem(index)">
            <i class="ti ti-x"></i>
          </a>
        </div>
        <div class="card-body">
          <!-- render array item based on its schema -->
          <CustomJsonFormsRenderer
            v-if="schema.items"
            :schema="schema.items"
            :data="item"
            @change="(newData) => setArrayItem(index, newData)" />
        </div>
      </div>

      <div v-if="getArrayData().length === 0" class="text-muted text-center py-4">
        {{ $t('publication.editor.no_items_hint') }}
      </div>
    </div>

    <!-- object renderer -->
    <div v-else-if="isObject(schema)" class="object-renderer">
      <div v-if="schema.description" class="form-text text-muted mb-3">
        {{ schema.description }}
      </div>
      <div v-for="(property, key) in schema.properties" :key="key" class="form-group mb-3">
        <!-- icon picker (string field flagged format: 'icon') -->
        <div v-if="hasType(property, 'string') && property.format === 'icon'">
          <label v-if="property.title || property.label || key" class="form-label">
            {{ property.title || property.label || key }}
          </label>
          <PopsIconPicker
            :modelValue="(getValue(key) as string) ?? ''"
            :iconsUrl="iconsUrl"
            @update:modelValue="setValue(key, $event)" />
          <small v-if="property.description" class="form-text text-muted">{{ property.description }}</small>
        </div>

        <!-- string input -->
        <FormInput
          v-else-if="hasType(property, 'string') && !property.enum && !property.oneOf?.length"
          :id="`field-${key}`"
          :label="property.title || property.label || key"
          :isRequired="isRequired(key)"
          :modelValue="getValue(key)"
          @update:modelValue="setValue(key, $event)"
          :hint="property.description"
          :placeholder="property.placeholder"
          :type="getInputType(property)"
          :maxLength="getInputType(property) === 'textarea' ? property.maxLength : undefined"
          :errors="getFieldErrors(key)"
          clearable
          :clearableValue="''" />

        <!-- number input -->
        <FormInput
          v-else-if="hasType(property, 'number') || hasType(property, 'integer')"
          :id="`field-${key}`"
          :label="property.title || property.label || key"
          :isRequired="isRequired(key)"
          :modelValue="getValue(key) ?? ''"
          @update:modelValue="setValue(key, parseNumber($event))"
          :hint="property.description"
          :placeholder="property.placeholder"
          type="number"
          :step="hasType(property, 'integer') ? '1' : 'any'"
          :errors="getFieldErrors(key)"
          clearable
          :clearableValue="''" />

        <!-- boolean switch -->
        <FormInput
          v-else-if="hasType(property, 'boolean')"
          :id="`field-${key}`"
          :label="property.title || property.label || key"
          :text="property.description"
          type="switch"
          :modelValue="getValue(key)"
          @update:modelValue="setValue(key, $event)"
          :errors="getFieldErrors(key)" />

        <!-- select dropdown (oneOf with const/title) -->
        <FormSelect
          v-else-if="property.oneOf?.length"
          :id="`field-${key}`"
          :label="property.title || property.label || key"
          :isRequired="isRequired(key)"
          :modelValue="getValue(key)"
          @update:modelValue="setValue(key, $event)"
          :options="property.oneOf.map((entry) => ({ value: entry.const, label: entry.title || String(entry.const) }))"
          :hint="property.description"
          :placeholder="property.placeholder || $t('publication.editor.please_select')"
          :errors="getFieldErrors(key)" />

        <!-- select dropdown (enum) -->
        <FormSelect
          v-else-if="property.enum"
          :id="`field-${key}`"
          :label="property.title || property.label || key"
          :isRequired="isRequired(key)"
          :modelValue="getValue(key)"
          @update:modelValue="setValue(key, $event)"
          :options="property.enum.map((value) => ({ value, label: value }))"
          :hint="property.description"
          :placeholder="property.placeholder || $t('publication.editor.please_select')"
          :errors="getFieldErrors(key)" />

        <!-- nested array input -->
        <div v-else-if="hasType(property, 'array')" class="array-field">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <small class="text-muted">{{ property.title || property.label || key }}</small>
            <button
              v-if="!property.items?.enum"
              type="button"
              class="btn btn-dark btn-pill btn-sm"
              @click="addArrayItem(key)">
              <i class="ti ti-plus ti-m"></i>
            </button>
          </div>

          <div v-if="property.description" class="form-text text-muted mb-2">
            <small>{{ property.description }}</small>
          </div>

          <!-- handle arrays with enum items (checkbox multi-select) -->
          <div v-if="property.items?.enum" class="enum-array-checkboxes">
            <div v-for="option in property.items.enum" :key="option" class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                :id="`field-${key}-${option}`"
                :checked="getArrayValue(key).includes(option)"
                @change="toggleEnumArrayItem(key, option)" />
              <label class="form-check-label" :for="`field-${key}-${option}`">
                {{ option }}
              </label>
            </div>
          </div>

          <!-- handle arrays of objects -->
          <div v-else-if="property.items && hasType(property.items, 'object')" class="nested-array-objects">
            <div v-for="(item, index) in getArrayValue(key)" :key="index" class="card mb-2">
              <div class="card-header d-flex justify-content-between align-items-center py-2 px-3">
                <small class="text-muted mb-0">{{ $t('publication.editor.item') }} {{ index + 1 }}</small>
                <a type="button" class="icon-link text-danger" @click="removeArrayItem(key, index)">
                  <i class="ti ti-x ti-m"></i>
                </a>
              </div>
              <div class="card-body p-3">
                <CustomJsonFormsRenderer
                  :schema="property.items"
                  :data="item"
                  @change="(newData) => setArrayItem(key, index, newData)" />
              </div>
            </div>
            <div v-if="getArrayValue(key).length === 0" class="text-muted text-center py-2">
              <small>{{ $t('publication.editor.no_items') }}</small>
            </div>
          </div>

          <!-- handle arrays of simple values -->
          <div v-else class="nested-array-simple">
            <div v-for="(item, index) in getArrayValue(key)" :key="index" class="d-flex mb-2">
              <FormInput
                :modelValue="item"
                @update:modelValue="setArrayItem(key, index, $event)"
                :placeholder="`${$t('publication.editor.item')} ${index + 1}`"
                class="flex-grow-1 me-2" />
              <a type="button" class="icon-link text-danger" @click="removeArrayItem(key, index)">
                <i class="ti ti-x"></i>
              </a>
            </div>
            <div v-if="getArrayValue(key).length === 0" class="text-muted text-center py-2">
              <small>{{ $t('publication.editor.no_items') }}</small>
            </div>
          </div>
        </div>

        <!-- nested object input -->
        <div v-else-if="hasType(property, 'object')" class="nested-object-field">
          <label class="form-label">{{ property.title || property.label || key }}</label>
          <div v-if="property.description" class="form-text text-muted mb-3">
            {{ property.description }}
          </div>
          <CustomJsonFormsRenderer :schema="property" :data="getValue(key)" @change="setValue(key, $event)" />
        </div>

        <!-- fallback textarea for complex types -->
        <FormInput
          v-else
          :id="`field-${key}`"
          :label="property.title || property.label || key"
          :isRequired="isRequired(key)"
          :modelValue="JSON.stringify(getValue(key), null, 2)"
          @update:modelValue="setJsonValue(key, $event)"
          :hint="property.description"
          :placeholder="property.placeholder"
          type="textarea"
          :errors="getFieldErrors(key)" />
      </div>
    </div>

    <!-- simple value renderer for non-object schemas -->
    <div v-else class="simple-renderer">
      <FormInput
        :modelValue="typeof data === 'object' ? JSON.stringify(data, null, 2) : data"
        @update:modelValue="handleSimpleChange"
        :hint="schema.description"
        :placeholder="schema.placeholder"
        :type="getSimpleInputType()" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { FormInput, FormSelect } from '@prospective/lithium';
import { PopsIconPicker } from '@prospective/pops-client-vue';
import { hostname } from '@/api/client';

// the icon manifest/svgs are served by the pjas backend, which is a different
// origin than the LEAD frontend on stage/prod — point the picker at the backend
// host explicitly instead of the default same-origin relative '/v1/icons'.
const iconsUrl = `${hostname}/v1/icons`;

/* ------------------------------ INTERFACES ------------------------------ */

interface OneOfEntry {
  const: any;
  title?: string;
}

interface JsonSchema {
  type?: string | string[];
  title?: string;
  label?: string;
  description?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  enum?: any[];
  oneOf?: OneOfEntry[];
  items?: JsonSchema;
  default?: any;
  placeholder?: string;
  format?: string;
  maxLength?: number;
}

/* ------------------------------ PROPS / EMITS ------------------------------ */

const props = defineProps<{
  schema: JsonSchema;
  data: any;
  errors?: Record<string, string[]> | null;
}>();

const emit = defineEmits<{
  change: [data: any];
}>();

/* ------------------------------ COMPUTED ------------------------------ */

const localData = computed({
  get: () => {
    // if we have data, use it, but merge with defaults for missing properties
    if (props.data != null) {
      if (hasType(props.schema, 'object') && props.schema.properties && typeof props.data === 'object') {
        const mergedData = { ...props.data };
        for (const [key, property] of Object.entries(props.schema.properties)) {
          if (mergedData[key] === undefined && property.default !== undefined) {
            mergedData[key] = property.default;
          }
        }
        return mergedData;
      }
      return props.data;
    }

    // otherwise, create default value based on schema
    if (hasType(props.schema, 'array')) {
      return [];
    } else if (hasType(props.schema, 'object')) {
      const defaultObj: any = {};
      if (props.schema.properties) {
        for (const [key, property] of Object.entries(props.schema.properties)) {
          const defaultValue = getDefaultValueForProperty(property);
          if (property.default !== undefined) {
            defaultObj[key] = defaultValue;
          }
        }
      }
      return defaultObj;
    } else {
      return getDefaultValueForProperty(props.schema);
    }
  },
  set: (value) => emit('change', value),
});

/* ------------------------------ HELPER FUNCTIONS ------------------------------ */

function hasType(schema: JsonSchema, type: string): boolean {
  if (Array.isArray(schema.type)) return schema.type.includes(type);
  return schema.type === type;
}

function isObject(schema: JsonSchema): boolean {
  return hasType(schema, 'object') && !!schema.properties;
}

function isArray(schema: JsonSchema): boolean {
  return hasType(schema, 'array') && !!schema.items;
}

function getArrayData(): any[] {
  return Array.isArray(localData.value) ? localData.value : [];
}

function getDefaultValueForProperty(property: JsonSchema): any {
  // if there's an explicit default, use it
  if (property.default !== undefined) {
    return property.default;
  }

  // oneOf with const values — default to the first option
  if (property.oneOf?.length) {
    return property.oneOf[0].const;
  }

  // nullable types default to null
  if (hasType(property, 'null')) return null;

  // otherwise, use type-based defaults
  if (hasType(property, 'string')) return '';
  else if (hasType(property, 'number') || hasType(property, 'integer')) return null;
  else if (hasType(property, 'boolean')) return false;
  else if (hasType(property, 'array')) return [];
  else if (hasType(property, 'object')) {
    const defaultObj: any = {};
    if (property.properties) {
      for (const [key, propSchema] of Object.entries(property.properties)) {
        if (propSchema.default !== undefined) {
          defaultObj[key] = getDefaultValueForProperty(propSchema);
        }
      }
    }
    return defaultObj;
  }
  return null;
}

function getDefaultItemValue(): any {
  if (!props.schema.items) return {};

  const itemSchema = props.schema.items;
  if (hasType(itemSchema, 'object')) {
    const defaultObj: any = {};
    if (itemSchema.properties) {
      for (const [key, property] of Object.entries(itemSchema.properties)) {
        defaultObj[key] = getDefaultValueForProperty(property);
      }
    }
    return defaultObj;
  } else {
    return getDefaultValueForProperty(itemSchema);
  }
}

/* ------------------------------ ARRAY METHODS ------------------------------ */

function setArrayItem(keyOrIndex: string | number, indexOrValue: number | any, value?: any): void {
  if (typeof keyOrIndex === 'string') {
    const key = keyOrIndex;
    const index = indexOrValue as number;
    const array = [...getArrayValue(key)];
    array[index] = value;
    setValue(key, array);
  } else {
    const index = keyOrIndex;
    const newValue = indexOrValue;
    const array = [...getArrayData()];
    array[index] = newValue;
    localData.value = array;
  }
}

function addArrayItem(key?: string): void {
  if (key) {
    const array = [...getArrayValue(key)];
    const property = props.schema.properties?.[key];

    if (property?.items && hasType(property.items, 'object')) {
      const defaultObj: any = {};
      if (property.items.properties) {
        for (const [propKey, propSchema] of Object.entries(property.items.properties)) {
          defaultObj[propKey] = getDefaultValueForProperty(propSchema);
        }
      }
      array.push(defaultObj);
    } else {
      const defaultValue = property?.items ? getDefaultValueForProperty(property.items) : '';
      array.push(defaultValue);
    }
    setValue(key, array);
  } else {
    const array = [...getArrayData()];
    const defaultValue = getDefaultItemValue();
    array.push(defaultValue);
    localData.value = array;
  }
}

function toggleEnumArrayItem(key: string, option: string): void {
  const array = [...getArrayValue(key)];
  const idx = array.indexOf(option);
  if (idx === -1) {
    array.push(option);
  } else {
    array.splice(idx, 1);
  }
  setValue(key, array);
}

function removeArrayItem(indexOrKey: string | number, index?: number): void {
  if (typeof indexOrKey === 'string' && typeof index === 'number') {
    const key = indexOrKey;
    const array = [...getArrayValue(key)];
    array.splice(index, 1);
    setValue(key, array);
  } else if (typeof indexOrKey === 'number') {
    const idx = indexOrKey;
    const array = [...getArrayData()];
    array.splice(idx, 1);
    localData.value = array;
  }
}

/* ------------------------------ FORM METHODS ------------------------------ */

function isRequired(key: string): boolean {
  return Array.isArray(props.schema.required) && props.schema.required.includes(key);
}

function getValue(key: string): any {
  return localData.value[key];
}

function setValue(key: string, value: any): void {
  const newData = { ...localData.value };
  newData[key] = value;
  localData.value = newData;
}

function getInputType(property: JsonSchema): string {
  if (hasType(property, 'string')) {
    if (property.format === 'textarea') return 'textarea';
    if (property.format === 'uri' || property.description?.toLowerCase().includes('url')) return 'url';
    if (property.description?.toLowerCase().includes('email')) return 'email';
    if (property.description?.toLowerCase().includes('password')) return 'password';
  }
  return 'text';
}

function parseNumber(value: any): number | null {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return isNaN(parsed) ? null : parsed;
}

function getArrayValue(key: string): any[] {
  const value = getValue(key);
  return Array.isArray(value) ? value : [];
}

function setJsonValue(key: string, value: string): void {
  try {
    const parsed = JSON.parse(value);
    setValue(key, parsed);
  } catch {
    setValue(key, value);
  }
}

function getSimpleInputType(): string {
  if (hasType(props.schema, 'number') || hasType(props.schema, 'integer')) return 'number';
  if (hasType(props.schema, 'boolean')) return 'switch';
  return 'text';
}

function getFieldErrors(key: string): string[] {
  return props.errors?.[key] ?? [];
}

function handleSimpleChange(value: any): void {
  if (hasType(props.schema, 'number') || hasType(props.schema, 'integer')) {
    localData.value = parseNumber(value);
  } else if (hasType(props.schema, 'boolean')) {
    localData.value = Boolean(value);
  } else if (hasType(props.schema, 'object')) {
    try {
      localData.value = JSON.parse(value);
    } catch {
      localData.value = value;
    }
  } else {
    localData.value = value;
  }
}
</script>

<style scoped>
.custom-jsonforms-renderer {
  width: 100%;
}

.array-field {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.nested-object-field {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
}

.nested-array-objects .card {
  border: 1px solid #e0e0e0;
  background-color: #fff;
}

.nested-array-objects .card-header {
  border-bottom: 1px solid #e0e0e0;
}

.nested-array-simple {
  background-color: #fff;
  border-radius: 0.25rem;
  padding: 0.5rem;
}

.array-renderer .card {
  border: 1px solid #dee2e6;
}

.array-renderer .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.75rem;
}

.array-renderer .card-body {
  padding: 1rem;
}

.form-group:last-child {
  margin-bottom: 0 !important;
}

.icon-link {
  color: #6c757d;
  text-decoration: none;
}

.icon-link.text-danger:hover {
  color: #dc3545 !important;
}
</style>
